import { NomadDB } from '~/db/NomadDB';
import type { CategorySchema } from '@/types/schema';
import type { CategoryAssociatedIds } from '~/types/quiz';
import { populate } from '~/lib/category/populate';
import { retrieveMediaIdsFromPopulateCategory } from '~/lib/media/index';
import { reduceQuizzes } from '~/lib/category/reduce';
import {
  cleanEmptyNotManagedOutDatedCategories,
  NEED_REFACTOR_TREE_CATEGORIES,
} from '~/lib/category/clean-and-refactor';

import { getAssociatedIds } from '~/lib/category/associated';
import { getServices } from '~/db/services';

export const useCategoryPopulate = () => {
  const { $db } = useNuxtApp();
  const category = ref<CategorySchema>();
  const mediaIds = ref<string[]>();
  const associatedIds = ref<CategoryAssociatedIds>();

  const { categoriesService } = getServices($db);

  const unload = () => {
    category.value = null;
    mediaIds.value = [];
    associatedIds.value = {
      quizIds: [],
      postIds: [],
      exerciseIds: [],
    };
  };

  const fetch = async (
    params: { id?: string; slug?: string; libraryBookId: string },
    displayHome = false,
    bReduceQuizzes = true,
    bGetAssociatedIds = true,
    isStudyContent: boolean = false
  ) => {
    let data = await categoriesService.get(params);
    const { libraryBookId } = params;
    if (!libraryBookId) {
      return false;
    }
    if (
      data &&
      data.contentType !== ContentTypes.SubChapter &&
      data.contentType !== ContentTypes.Chapter &&
      data?.children &&
      data.children.length === 1 &&
      !data?.essentialsArray?.length &&
      !data?.annalsArray?.length &&
      data.children[0].type === 'category' &&
      !NEED_REFACTOR_TREE_CATEGORIES.includes(data.contentType)
    ) {
      const childParams = {
        libraryBookId: params.libraryBookId,
        id: data.children[0].id,
      };
      data = await categoriesService.get(childParams);
    }
    if (!data) return false;
    return fetchChildren(
      data,
      libraryBookId,
      displayHome,
      bReduceQuizzes,
      bGetAssociatedIds,
      isStudyContent
    );
  };

  const fetchChildren = async (
    data: CategorySchema,
    libraryBookId: string,
    displayHome = false,
    bReduceQuizzes = true,
    bGetAssociatedIds = true,
    isStudyContent: boolean = false
  ) => {
    if (data) {
      let { category: cat } = await populate(
        $db as NomadDB,
        data,
        libraryBookId,
        displayHome
      );

      // if (bReduceQuizzes) reduceQuizzes(cat);

      cat = cleanEmptyNotManagedOutDatedCategories(cat) as CategorySchema;

      const mdIds = retrieveMediaIdsFromPopulateCategory(cat);

      if (isStudyContent) {
        cat = filterStudyContent(cat);
      }

      category.value = cat;
      mediaIds.value = mdIds;

      if (bGetAssociatedIds) {
        associatedIds.value = getAssociatedIds(cat);
      }

      if (!category.value) return false;

      return true;
    } else {
      return false;
    }
  };

  const filterStudyContent = (cat: CategorySchema) => {
    const restrictedContentTypes = [
      ContentTypes.Discipline,
      ContentTypes.SuperDiscipline,
      ContentTypes.DisciplineSection,
      ContentTypes.Chapter,
    ];

    if (cat.contentType === ContentTypes.CourseAndQuiz) {
      const propToCheck = 'studyContent';
      cat.children = cat.children.reduce((acc, item) => {
        if (restrictedContentTypes.includes(item.contentType)) {
          if (!propToCheck) {
            acc.push(item);
          } else if (
            !Object.prototype.hasOwnProperty.call(item, propToCheck) ||
            item[propToCheck]
          ) {
            acc.push(item);
          }
        }
        return acc;
      }, []);
    }
    return cat;
  };

  const isLoaded = computed(() => {
    return category.value !== null;
  });
  return {
    associatedIds,
    category,
    mediaIds,
    fetch,
    fetchChildren,
    unload,
    isLoaded,
  };
};
